import * as React from "react";
import { Badge, Banner, Button, CardBody, Icon, Select } from "../components";
import type { SubjectChoiceModel } from "./SubjectChoices";
import * as Rules from "./subjectChoiceEvaluator";

/**
 * One student at a time, laid out the way a subject-choice form reads: a section per rule with a counter, radio
 * cards where the rule is "exactly one", toggle tiles elsewhere, and running totals for the rules that count every
 * subject or credits. Prev/Next (or the arrow keys) step through the students the search lets through.
 */
export const SubjectChoiceStudentTab: React.FC<{ model: SubjectChoiceModel }> = ({ model }) => {
    const { visible, choices, names, canWrite, isLocked, blockedReason, toggle, offering, subjectName } = model;
    const [studentID, setStudentID] = React.useState(visible[0] ?? "");
    const index = Math.max(0, visible.indexOf(studentID));
    const current = visible[index] ?? "";

    const step = React.useCallback(
        (offset: number) => {
            const next = visible[index + offset];
            if (next) setStudentID(next);
        },
        [visible, index]
    );

    // Arrow keys move between students, except while typing in a field.
    React.useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            const target = e.target as HTMLElement;
            if (["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName) && (target as HTMLInputElement).type !== "checkbox") return;
            if (e.key === "ArrowLeft") step(-1);
            if (e.key === "ArrowRight") step(1);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [step]);

    if (!current) return <CardBody>No students match the search.</CardBody>;

    const mine = choices[current] ?? [];
    const problems = model.findings(current);
    const credits = (id: string) => offering.subjects.find((s) => s.moduleID === id)?.credits ?? 0;
    const sectionRules = offering.rules.filter((r) => !r.appliesToAllSubjects && r.moduleIDs.length > 0);
    const totals = offering.rules.filter((r) => r.appliesToAllSubjects);
    const inARule = new Set(sectionRules.flatMap((r) => r.moduleIDs));
    const others = offering.subjects.filter((s) => !inARule.has(s.moduleID)).map((s) => s.moduleID);
    const active = (ruleID: string) => model.activeRules.some((r) => r.id === ruleID);

    const tile = (moduleID: string, radio: boolean) => {
        const on = mine.includes(moduleID);
        const locked = isLocked(current, moduleID);
        const blocked = blockedReason(current, moduleID);
        const disabled = !canWrite || locked || !!blocked;
        const credit = offering.subjects.find((s) => s.moduleID === moduleID)?.credits;
        return (
            <button
                key={moduleID}
                type="button"
                role={radio ? "radio" : "checkbox"}
                aria-checked={on}
                disabled={disabled}
                title={locked ? "A term of this subject is Completed and cannot be changed here" : blocked}
                onClick={() => toggle(current, moduleID, !on)}
                className={`flex items-center gap-2 rounded-md border-[1.5px] px-3 py-2 text-left text-sm transition-colors ${
                    on ? "border-primary bg-primary-soft text-primary-text" : "border-border bg-surface text-text hover:bg-surface-hover"
                } ${disabled ? "cursor-not-allowed opacity-70" : ""}`}
            >
                <Icon icon={["fad", radio ? (on ? "circle-dot" : "circle") : on ? "square-check" : "square"]} />
                <span className="flex-1">{subjectName(moduleID)}</span>
                {credit != null && <span className="text-xs text-text-weak">{credit} cr</span>}
                {locked && <Icon icon={["fad", "lock"]} />}
            </button>
        );
    };

    return (
        <CardBody>
            <div className="mb-4 flex flex-wrap items-center gap-2">
                <Button variant="outline-secondary" icon={["fad", "chevron-left"]} onClick={() => step(-1)} disabled={index === 0}>
                    Previous
                </Button>
                <div className="w-full max-w-xs">
                    <Select value={current} onChange={(e) => setStudentID(e.target.value)} aria-label="Student">
                        {visible.map((id) => (
                            <option key={id} value={id}>
                                {names(id)}
                            </option>
                        ))}
                    </Select>
                </div>
                <Button variant="outline-secondary" onClick={() => step(1)} disabled={index >= visible.length - 1}>
                    Next <Icon icon={["fad", "chevron-right"]} />
                </Button>
                <span className="text-sm text-text-weak">
                    {index + 1} of {visible.length} &middot; arrow keys move between students
                </span>
            </div>

            {problems.length > 0 ? (
                <Banner tone="danger" className="mb-4">
                    <ul className="list-disc pl-5">
                        {problems.map((f, i) => (
                            <li key={i}>{f.message}</li>
                        ))}
                    </ul>
                </Banner>
            ) : (
                <Banner tone="success" className="mb-4">
                    {`${names(current)} meets every rule.`}
                </Banner>
            )}

            {totals.length > 0 && (
                <div className="mb-6 flex flex-wrap gap-2">
                    {totals.map((rule) => {
                        const creditRule = Rules.isCreditRule(rule);
                        const value = creditRule ? mine.reduce((sum, id) => sum + credits(id), 0) : mine.filter((id) => offering.subjects.some((s) => s.moduleID === id)).length;
                        return (
                            <Badge key={rule.id} tone={problems.some((f) => f.ruleID === rule.id) ? "danger" : "success"}>
                                {`${rule.name}: ${value}${creditRule ? " credits" : ""} (${Rules.bounds(rule.minimum, rule.maximum)})`}
                            </Badge>
                        );
                    })}
                </div>
            )}

            <div className="grid gap-6">
                {sectionRules.map((rule) => {
                    const radio = Rules.isExactlyOne(rule);
                    const creditRule = Rules.isCreditRule(rule);
                    const taken = rule.moduleIDs.filter((id) => mine.includes(id));
                    const value = creditRule ? taken.reduce((sum, id) => sum + credits(id), 0) : taken.length;
                    const broken = problems.some((f) => f.ruleID === rule.id);
                    return (
                        <section key={rule.id}>
                            <div className="mb-2 flex flex-wrap items-baseline gap-2">
                                <h3 className="font-semibold text-text-strong">{rule.name}</h3>
                                <span className={`text-sm ${broken ? "text-danger-text" : "text-text-weak"}`}>
                                    {creditRule
                                        ? `${value} credits (needs ${Rules.bounds(rule.minimum, rule.maximum)})`
                                        : `${value} chosen (${radio ? "choose 1" : Rules.bounds(rule.minimum, rule.maximum)})`}
                                </span>
                                {!active(rule.id) && <Badge tone="neutral">Not checked this year</Badge>}
                            </div>
                            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3" role={radio ? "radiogroup" : "group"} aria-label={rule.name}>
                                {rule.moduleIDs.map((id) => tile(id, radio))}
                            </div>
                        </section>
                    );
                })}

                {others.length > 0 && (
                    <section>
                        <h3 className="mb-2 font-semibold text-text-strong">{sectionRules.length ? "Other subjects" : "Subjects"}</h3>
                        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">{others.map((id) => tile(id, false))}</div>
                    </section>
                )}
            </div>
        </CardBody>
    );
};
